import { useAuth } from "@/hooks/auth";
import { useMarketProducersQuery } from "@/hooks/metadata";
import { usePreferenceMutation, usePreferenceQuery } from "@/hooks/preference";
import Combobox, { MenuItem } from "@ingka/combobox";
import FormField from "@ingka/form-field";
import Modal, { ModalHeader, Sheets } from "@ingka/modal";
import LabelWithToolTip from "../LabelWithTooltip/LabelWithToolTip";
import { BelongsToMarketPrompt } from "./BelongsToMarketPrompt";

type CaresAboutMarketSheetProps = {
  modalOpen: boolean;
  onModalClosed: () => void;
};

export function PreferenceSheet({
  modalOpen,
  onModalClosed,
}: CaresAboutMarketSheetProps) {
  const { id: userId, isAdmin } = useAuth().user;
  const marketProducersQuery = useMarketProducersQuery();
  const preferenceMutation = usePreferenceMutation();
  const preferenceQuery = usePreferenceQuery();

  if (marketProducersQuery.isError || preferenceQuery.isError) {
    throw new Error(
      "Failed to load market producers metadata or user settings",
    );
  }

  if (!marketProducersQuery.isSuccess || !preferenceQuery.isSuccess) {
    return null;
  }

  const { caresAboutMarket = [], notificationForMarket = [] } =
    preferenceQuery.data || {};

  const marketMappingFn = (producerId: number) => {
    const result = marketProducersQuery.data.find(
      (item) => item.id === producerId,
    );
    return result;
  };

  const selectedMarkets = caresAboutMarket
    .map(marketMappingFn)
    .filter((item) => item !== undefined);
  const selectedNotificationMarkets = notificationForMarket
    .map(marketMappingFn)
    .filter((item) => item !== undefined);

  return (
    <>
      {isAdmin && !(preferenceQuery.data?.belongsToMarket > 0) && (
        <BelongsToMarketPrompt onSuccess={() => {}} />
      )}
      <Modal
        visible={modalOpen}
        escapable={true}
        handleCloseBtn={onModalClosed}
      >
        <Sheets
          header={<ModalHeader title="Administrator Settings" />}
          footer={null}
          size="small"
          aria-label="Administrator Settings"
        >
          <FormField
            className="m-space150 mt-space300"
            fieldHelper={{
              id: "market-filter-helper-msg-id",
              msg:
                selectedMarkets.length > 0
                  ? `Selected: ${selectedMarkets.map((item) => item?.name).join(", ")}`
                  : "",
            }}
          >
            <Combobox
              multiple
              labelId="market-filter-request-id"
              listboxId="market-filter-request-id"
              label={
                <LabelWithToolTip
                  labelText="Filter requests"
                  toolTipPosition="trailing"
                  toolTipText="In this setting you can customize your overview by applying a filter to the requests. This allows you to view only the requests coming from your market. You can select one or more markets or leave it unselected to view all requests"
                />
              }
              value={selectedMarkets.map((item) => `_${item.id}`)}
              onListBoxChange={async (selectedItems: string[]) => {
                await preferenceMutation.mutateAsync({
                  userId,
                  caresAboutMarket: selectedItems.map((item) =>
                    Number(item.slice(1)),
                  ),
                });
              }}
              onClearClicked={async () =>
                await preferenceMutation.mutateAsync({
                  userId,
                  caresAboutMarket: [],
                })
              }
            >
              <>
                {marketProducersQuery.data.map((item) => (
                  <MenuItem
                    id={`_${item.id}`}
                    key={`${item.name}-${item.id}`}
                    title={item.name}
                  />
                ))}
              </>
            </Combobox>
          </FormField>
          <FormField
            className="m-space150 mt-space300"
            fieldHelper={{
              id: "notify-market-helper-msg-id",
              msg:
                selectedNotificationMarkets.length > 0
                  ? `Selected: ${selectedNotificationMarkets.map((item) => item?.name).join(", ")}`
                  : "",
            }}
          >
            <Combobox
              multiple
              labelId="notify-market-id"
              listboxId="notify-market-id"
              label={
                <LabelWithToolTip
                  labelText="Incoming request notifications"
                  toolTipPosition="trailing"
                  toolTipText="In this setting you can select one or more markets to receive notifications when a new request is created. If you don’t select any, you won’t receive notifications. Notifications will be shared via email and within the Uploader tool."
                />
              }
              value={selectedNotificationMarkets.map((item) => `_${item.id}`)}
              onListBoxChange={async (selectedItems: string[]) => {
                await preferenceMutation.mutateAsync({
                  userId,
                  notificationForMarket: selectedItems.map((item) =>
                    Number(item.slice(1)),
                  ),
                });
              }}
              onClearClicked={async () =>
                await preferenceMutation.mutateAsync({
                  userId,
                  notificationForMarket: [],
                })
              }
            >
              <>
                {marketProducersQuery.data.map((item) => (
                  <MenuItem
                    id={`_${item.id}`}
                    key={`${item.name}-${item.id}`}
                    title={item.name}
                  />
                ))}
              </>
            </Combobox>
          </FormField>
        </Sheets>
      </Modal>
    </>
  );
}
